import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Users, Search, Trash2, Edit, AlertCircle, User, Mail, Calendar, Shield, ToggleLeft, ToggleRight, Clock
} from 'lucide-react';
import toast from 'react-hot-toast';
import memberService from '../services/memberService';
import roleService from '../services/roleService';
import ResponsiveModal from './ResponsiveModal';
import EnhancedDataTable from './EnhancedDataTable';
import CommonLoader from './CommonLoader';

const DataroomMembersAdmin = ({ dataroomUuid }) => {
  const [members, setMembers] = useState([]);
  const [summary, setSummary] = useState({ total: 0, active: 0, inactive: 0 });
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState(''); // '' for all, 1 for active, 0 for inactive
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 15,
    total: 0,
    last_page: 1
  });

  // Modal states
  const [showEditRoleModal, setShowEditRoleModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form states
  const [selectedRoleId, setSelectedRoleId] = useState('');

  useEffect(() => {
    if (dataroomUuid) {
      fetchMembers();
      fetchRoles();
    }
  }, [dataroomUuid, pagination.current_page, searchTerm, roleFilter, statusFilter]);

  const fetchMembers = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.current_page,
        per_page: pagination.per_page,
        ...(searchTerm && { search: searchTerm }),
        ...(roleFilter && { role_id: roleFilter }),
        ...(statusFilter !== '' && { is_active: statusFilter })
      };

      const response = await memberService.adminDataroom.list(dataroomUuid, params);
      
      // Handle response structure
      if (response) {
        setMembers(response.members || []);
        setSummary(response.summary || { total: 0, active: 0, inactive: 0 });
        
        // Handle pagination
        if (response.pagination) {
          setPagination(response.pagination);
        }
      }
    } catch (error) {
      
      if (error.response?.status === 404) {
        toast.error('Members endpoint not found. Please check API configuration.');
      } else if (error.response?.status === 401) {
        toast.error('Unauthorized. Please check your permissions.');
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to load members. Please try again.');
      }
      
      setMembers([]);
      setPagination({
        current_page: 1,
        per_page: 15,
        total: 0,
        last_page: 1
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await roleService.dataroom.list(dataroomUuid);
      let rolesData = [];
      if (response) {
        if (response.roles && Array.isArray(response.roles)) {
          rolesData = response.roles;
        } else if (response.data && Array.isArray(response.data)) {
          rolesData = response.data;
        } else if (Array.isArray(response)) {
          rolesData = response;
        }
      }
      setRoles(rolesData);
    } catch (error) {
      setRoles([]);
    }
  };

  const handleEditRole = (member) => {
    if (!member) {
      toast.error('Invalid member data');
      return;
    }
    setSelectedMember(member);
    setSelectedRoleId(member.dataroom_role_id || member.role_id || '');
    setShowEditRoleModal(true);
  };

  const handleRemoveMember = (member) => {
    if (!member) {
      toast.error('Invalid member data');
      return;
    }
    setSelectedMember(member);
    setShowDeleteModal(true);
  };

  const handleToggleStatus = async (member) => {
    if (!member) {
      toast.error('Invalid member data');
      return;
    }
    
    const userId = member.user_id || member.id;
    // Use is_active_text to determine current status
    const currentStatus = member.is_active_text === 'Active';
    
    try {
      await memberService.adminDataroom.toggleUserStatus(dataroomUuid, userId);
      toast.success(`User ${currentStatus ? 'deactivated' : 'activated'} successfully`);
      fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to toggle user status');
    }
  };

  const handleSubmitRoleUpdate = async (e) => {
    e.preventDefault();
    
    if (!selectedRoleId) {
      toast.error('Please select a role');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      await memberService.adminDataroom.updateRole(
        dataroomUuid, 
        selectedMember.user_id || selectedMember.id, 
        selectedRoleId
      );
      toast.success('Member role updated successfully');
      setShowEditRoleModal(false);
      fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update member role');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmRemove = async () => {
    if (!selectedMember) {
      toast.error('No member selected');
      return;
    }
    
    setIsSubmitting(true);
    try {
      await memberService.adminDataroom.remove(
        dataroomUuid, 
        selectedMember.user_id || selectedMember.id
      );
      toast.success('Member removed successfully');
      setShowDeleteModal(false);
      setSelectedMember(null);
      fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to remove member');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const columns = [
    {
      key: 'user',
      header: 'Member Details',
      sortable: false,
      render: (value, member) => {
        const user = member.user || member;
        if (!user) return <span className="text-gray-400">N/A</span>;
        return (
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
              <User className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="font-medium text-gray-900">
                {user.first_name || user.name} {user.last_name || ''}
              </p>
              <p className="text-sm text-gray-500">{user.email}</p>
              {user.phone_no && (
                <p className="text-xs text-gray-400 mt-1">📱 {user.phone_no}</p>
              )}
            </div>
          </div>
        );
      }
    },
    {
      key: 'role',
      header: 'Role & Permissions',
      sortable: true,
      render: (value, member) => {
        const role = member.role || member.dataroom_role;
        
        // If user has a role, display it
        if (role) {
          return (
            <div>
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                <Shield className="w-3 h-3 mr-1" />
                {role.display_name || role.name || 'Member'}
              </span>
              {role.permissions && role.permissions.length > 0 && (
                <div className="mt-2">
                  <div className="text-xs text-gray-500 mb-1">
                    Permissions ({role.permissions.length}):
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {role.permissions.slice(0, 3).map(perm => (
                      <span key={perm.id} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                        {perm.display_name || perm.name}
                      </span>
                    ))}
                    {role.permissions.length > 3 && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                        +{role.permissions.length - 3} more
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        }
        
        // If user has custom permissions without a role
        if (member.custom_permissions && member.custom_permissions.length > 0) {
          return (
            <div>
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                <Shield className="w-3 h-3 mr-1" />
                Custom Permissions
              </span>
              <div className="mt-1 text-xs text-gray-500">
                {member.custom_permissions.length} permissions
              </div>
            </div>
          );
        }
        
        // No role and no permissions
        return <span className="text-gray-400 text-sm">No role assigned</span>;
      }
    },
    {
      key: 'joined_at',
      header: 'Joined Date',
      sortable: true,
      render: (value, member) => {
        const dateStr = member?.joined_at || member?.created_at;
        if (!dateStr) return <span className="text-gray-400">N/A</span>;
        return (
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-gray-400" />
            <span className="text-sm text-gray-600">{formatDate(dateStr)}</span>
          </div>
        );
      }
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (value, member) => {
        // Show direct is_active_text value from API
        const statusText = member.is_active_text;
        const isActive = statusText === 'Active';
        
        return (
          <div className="space-y-1">
            <button
              onClick={() => handleToggleStatus(member)}
              className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                isActive
                  ? 'bg-green-100 text-green-800 hover:bg-green-200'
                  : 'bg-red-100 text-red-800 hover:bg-red-200'
              }`}
              title={`Click to ${isActive ? 'deactivate' : 'activate'}`}
            >
              {isActive ? (
                <ToggleRight className="w-3.5 h-3.5 mr-1.5" />
              ) : (
                <ToggleLeft className="w-3.5 h-3.5 mr-1.5" />
              )}
              {statusText}
            </button>
            {member.invitation_id && (
              <div className="text-xs text-gray-500">
                Invite ID: #{member.invitation_id}
              </div>
            )}
          </div>
        );
      }
    }
  ];

  const actions = (member) => {
    return [
      {
        icon: Edit,
        label: 'Change Role',
        onClick: () => handleEditRole(member)
      },
      {
        icon: Trash2,
        label: 'Remove',
        onClick: () => handleRemoveMember(member),
        className: 'text-red-600 hover:text-red-800'
      }
    ];
  };

  if (loading) {
    return <CommonLoader />;
  }

  return (
    <>
      <div className="space-y-6">

      {/* Page Header */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Dataroom Members</h2>
            <p className="mt-1 text-sm text-gray-600">
              Manage all members with access to this dataroom
            </p>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Users className="w-5 h-5" />
            <span className="font-medium">{pagination.total || 0} Total Members</span>
          </div>
        </div>
      </div>

      {/* Content Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          {/* Search and Filters */}
          <div className="flex flex-col sm:flex-row gap-4 justify-between mb-6">
            <div className="flex flex-1 gap-4">
              {/* Search */}
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                />
              </div>

              {/* Role Filter */}
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
              >
                <option value="">All Roles</option>
                {roles.map(role => (
                  <option key={role.id} value={role.id}>
                    {role.display_name || role.name}
                  </option>
                ))}
              </select>

              {/* Status Filter */}
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPagination(prev => ({ ...prev, current_page: 1 }));
                }}
                className="px-3 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
              >
                <option value="">All Status</option>
                <option value="1">Active</option>
                <option value="0">Inactive</option>
              </select>
            </div>
          </div>

          {/* Members Table */}
          <EnhancedDataTable
            data={members}
            columns={columns}
            actions={actions}
            emptyMessage="No members found in this dataroom"
            emptyIcon={Users}
            showColumnControls={true}
            enableColumnResize={true}
            enableDragDrop={true}
            pagination={pagination}
            onPageChange={(page) => setPagination(prev => ({ ...prev, current_page: page }))}
            itemName="members"
            onSort={(key, direction) => {
              const sorted = [...members].sort((a, b) => {
                let aVal = a[key];
                let bVal = b[key];
                
                if (key === 'joined_at') {
                  aVal = aVal ? new Date(aVal).getTime() : 0;
                  bVal = bVal ? new Date(bVal).getTime() : 0;
                } else if (key === 'role') {
                  aVal = a.role?.display_name || a.role?.name || '';
                  bVal = b.role?.display_name || b.role?.name || '';
                } else if (key === 'status') {
                  aVal = a.is_active_text || '';
                  bVal = b.is_active_text || '';
                }
                
                if (aVal < bVal) return direction === 'asc' ? -1 : 1;
                if (aVal > bVal) return direction === 'asc' ? 1 : -1;
                return 0;
              });
              setMembers(sorted);
            }}
            showRefresh={true}
            onRefresh={() => {
              fetchMembers();
              fetchRoles();
            }}
          />
          
        </div>
      </div>

      {/* Edit Role Modal */}
      <ResponsiveModal
        isOpen={showEditRoleModal}
        onClose={() => {
          setShowEditRoleModal(false);
          setSelectedMember(null);
          setSelectedRoleId('');
        }}
        title="Change Member Role"
        icon={Shield}
      >
        <form onSubmit={handleSubmitRoleUpdate}>
          {selectedMember && (
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">Member:</p>
              <p className="font-medium text-gray-900">
                {selectedMember.user?.first_name || selectedMember.first_name || selectedMember.name} {selectedMember.user?.last_name || selectedMember.last_name || ''}
              </p>
              <p className="text-sm text-gray-500">{selectedMember.user?.email || selectedMember.email}</p>
            </div>
          )}
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Role <span className="text-red-500">*</span>
            </label>
            <select
              value={selectedRoleId}
              onChange={(e) => setSelectedRoleId(e.target.value)}
              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            >
              <option value="">Select a role</option>
              {roles.map(role => (
                <option key={role.id} value={role.id}>
                  {role.display_name || role.name}
                </option>
              ))}
            </select>
          </div>
          
          <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={() => {
                setShowEditRoleModal(false);
                setSelectedMember(null);
                setSelectedRoleId('');
              }}
              className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-3 py-1.5 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Updating...' : 'Update Role'}
            </button>
          </div>
        </form>
      </ResponsiveModal>

      {/* Delete Confirmation Modal */}
      <ResponsiveModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedMember(null);
        }}
        title="Remove Member"
        icon={AlertCircle}
        iconColor="text-red-600"
      >
        <p className="text-gray-600 mb-6">
          Are you sure you want to remove{' '}
          <span className="font-medium">
            {selectedMember?.user?.first_name || selectedMember?.first_name || selectedMember?.name} {selectedMember?.user?.last_name || selectedMember?.last_name || ''}
          </span>{' '}
          from this dataroom? They will lose access to all dataroom resources.
        </p>
        <div className="flex justify-end gap-3 pt-4">
          <button
            onClick={() => {
              setShowDeleteModal(false);
              setSelectedMember(null);
            }}
            className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirmRemove}
            disabled={isSubmitting}
            className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Removing...' : 'Remove Member'}
          </button>
        </div>
      </ResponsiveModal>
    </>
  );
};

export default DataroomMembersAdmin;